// strongly-ai-skill-scorer
//
// Single source of truth for grading Claude skills. Merges the calibrated
// rule set from strongly-ai-skills with the structured-Skill input shape and
// discoverability checks (tags / category / mcpTools) from strongly-ai-frontend.
//
// Two entry points:
//   • scoreSkill(input: ScorerInput) — raw SKILL.md string (paste or github mode)
//   • scoreSkillObject(skill: SkillLike) — structured host Skill document
//
// Both return the same ScoreReport so consumers can render one UI.

import { analyzeArchitecture } from './analysis/architecture';
import { analyzeCohesion } from './analysis/cohesion';
import { analyzeSemantic } from './analysis/semantic';
import { analyzeStyle } from './analysis/style';
import { analyzeText } from './analysis/text';
import { analyzeTriggers } from './analysis/triggers';
import { parseSkillMd, asString } from './parser';
import { buildRecommendations } from './recommendations';
import { bodyRules } from './rules/body';
import { cohesionRules } from './rules/cohesion';
import { descriptionRules } from './rules/description';
import { discoverabilityRules } from './rules/discoverability';
import { lexicalRules } from './rules/lexical';
import { nameRules } from './rules/name';
import { progressiveDisclosureRules } from './rules/progressiveDisclosure';
import { safetyRules } from './rules/safety';
import { semanticRules } from './rules/semantic';
import { structureRules } from './rules/structure';
import { styleRules } from './rules/style';
import { triggerRules } from './rules/triggers';
import {
  CATEGORY_LABELS,
  CATEGORY_WEIGHTS,
  type CategoryId,
  type CategoryScore,
  type Finding,
  type Rule,
  type ScoreReport,
  type ScoringContext,
  type SkillMetadata,
} from './rules/types';
import { gradeFor, letterGradeFor } from './grade';
import { skillToScorerInput, type SkillLike } from './adapter';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type {
  ScoreReport,
  Finding,
  CategoryScore,
  Recommendation,
  Rule,
  CategoryId,
  Severity,
  FindingStatus,
  Grade,
  LetterGrade,
  SkillMetadata,
  ScoringContext,
} from './rules/types';
export { fetchFromGitHub, GitHubFetchError, parseGitHubUrl } from './github';
export { skillToSkillMd, skillToScorerInput, type SkillLike } from './adapter';
export { gradeFor, letterGradeFor } from './grade';
export { parseSkillMd, asString } from './parser';
export { CATEGORY_LABELS, CATEGORY_WEIGHTS } from './rules/types';

// Analysis layers — exported for calibration scripts and tooling that need
// direct access to text / triggers / architecture metrics.
export { analyzeText } from './analysis/text';
export type { TextMetrics } from './analysis/text';
export { analyzeTriggers } from './analysis/triggers';
export type { TriggerMetrics } from './analysis/triggers';
export { analyzeSemantic } from './analysis/semantic';
export type { SemanticMetrics } from './analysis/semantic';
export { analyzeStyle } from './analysis/style';
export type { StyleMetrics } from './analysis/style';
export { analyzeCohesion } from './analysis/cohesion';
export type { CohesionMetrics } from './analysis/cohesion';
export { analyzeArchitecture } from './analysis/architecture';
export type { ArchitectureMetrics, CodeBlock, Heading } from './analysis/architecture';

export interface ScorerInput {
  source: 'paste' | 'github';
  skillMd: string;
  directoryListing?: string[];
  folderName?: string;
  skillFilename?: string;
  subfolderEntries?: Record<string, Array<{ name: string; type: 'file' | 'dir'; size?: number }>>;
  /** Optional structured metadata supplied via skillToScorerInput(). */
  skillMetadata?: SkillMetadata;
}

export const RULESET_VERSION = '1.0.0';

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

const ALL_RULES: Rule[] = [
  ...structureRules,
  ...nameRules,
  ...descriptionRules,
  ...triggerRules,
  ...semanticRules,
  ...bodyRules,
  ...cohesionRules,
  ...lexicalRules,
  ...styleRules,
  ...progressiveDisclosureRules,
  ...safetyRules,
  ...discoverabilityRules,
];

function appliesToInput(rule: Rule, source: 'paste' | 'github'): boolean {
  if (!rule.appliesTo || rule.appliesTo === 'both') return true;
  return rule.appliesTo === source;
}

// ---------------------------------------------------------------------------
// Entry points
// ---------------------------------------------------------------------------

export function scoreSkill(input: ScorerInput): ScoreReport {
  const parsed = parseSkillMd(input.skillMd ?? '');
  const desc = asString(parsed.frontmatter.description);
  const bodyText = analyzeText(parsed.body);
  const arch = analyzeArchitecture(parsed.body);
  const ctx: ScoringContext = {
    source: input.source,
    skillMd: input.skillMd ?? '',
    parsed,
    directoryListing: input.directoryListing,
    folderName: input.folderName,
    skillFilename: input.skillFilename,
    subfolderEntries: input.subfolderEntries,
    descText: analyzeText(desc),
    bodyText,
    triggers: analyzeTriggers(desc),
    arch,
    cohesion: analyzeCohesion(desc, parsed.body),
    semantic: analyzeSemantic(desc),
    style: analyzeStyle({ descChars: desc.length, bodyText, arch }),
    skillMetadata: input.skillMetadata,
  };

  const findings: Finding[] = [];
  const categoryAgg: Record<CategoryId, { weight: number; earned: number }> = {
    structure: { weight: 0, earned: 0 },
    name: { weight: 0, earned: 0 },
    description: { weight: 0, earned: 0 },
    triggers: { weight: 0, earned: 0 },
    body: { weight: 0, earned: 0 },
    lexical: { weight: 0, earned: 0 },
    progressiveDisclosure: { weight: 0, earned: 0 },
    safety: { weight: 0, earned: 0 },
    discoverability: { weight: 0, earned: 0 },
  };

  let criticalFail = false;

  for (const rule of ALL_RULES) {
    if (!appliesToInput(rule, input.source)) {
      findings.push({
        id: rule.id,
        category: rule.category,
        severity: rule.severity,
        weight: rule.weight,
        title: rule.title,
        guideRef: rule.guideRef,
        status: 'skipped',
        message: 'Not applicable to this input type.',
      });
      continue;
    }

    const out = rule.run(ctx);
    const finding: Finding = {
      id: rule.id,
      category: rule.category,
      severity: rule.severity,
      weight: rule.weight,
      title: rule.title,
      guideRef: rule.guideRef,
      ...out,
    };
    findings.push(finding);

    if (finding.status === 'skipped') continue;

    categoryAgg[rule.category].weight += rule.weight;
    if (finding.status === 'pass') {
      categoryAgg[rule.category].earned += rule.weight;
    } else if (finding.status === 'warn') {
      // Severity-aware partial credit: warn rules sting more when the rule was a hard failure.
      const partial = rule.severity === 'fail' ? 0.1 : rule.severity === 'warn' ? 0.2 : 0.45;
      categoryAgg[rule.category].earned += rule.weight * partial;
    }

    if (
      finding.status === 'fail' &&
      rule.severity === 'fail' &&
      (rule.category === 'structure' ||
        rule.category === 'description' ||
        rule.category === 'name' ||
        rule.category === 'triggers')
    ) {
      criticalFail = true;
    }
  }

  const categories: CategoryScore[] = (Object.keys(CATEGORY_WEIGHTS) as CategoryId[]).map((id) => {
    const agg = categoryAgg[id];
    const ratio = agg.weight === 0 ? 1 : agg.earned / agg.weight;
    return {
      id,
      label: CATEGORY_LABELS[id],
      weight: CATEGORY_WEIGHTS[id],
      earned: Math.round(ratio * CATEGORY_WEIGHTS[id] * 10) / 10,
      ratio,
    };
  });

  const totalEarned = categories.reduce((acc, c) => acc + c.ratio * c.weight, 0);
  const totalWeight = categories.reduce((acc, c) => acc + c.weight, 0);
  let score = Math.round((totalEarned / totalWeight) * 100);

  // Structural floors. A SKILL.md missing the basics cannot benefit from
  // vacuous "no XML" / "no secrets" passes elsewhere in the rubric.
  const hasFrontmatter = Object.keys(parsed.frontmatter).length > 0;
  const hasName = typeof parsed.frontmatter.name === 'string' && parsed.frontmatter.name.trim().length > 0;
  const hasDesc = typeof parsed.frontmatter.description === 'string' && parsed.frontmatter.description.trim().length > 0;
  const hasBody = parsed.body.trim().length >= 100;

  if (!hasFrontmatter) score = Math.min(score, 8);
  else if (!hasName && !hasDesc) score = Math.min(score, 15);
  else if (!hasName || !hasDesc) score = Math.min(score, 30);
  if (!hasBody && hasFrontmatter) score = Math.min(score, 45);

  if (criticalFail) score = Math.min(score, 50);

  // Warning ceiling: too many warns prevents an "Excellent" badge regardless of
  // category math. Forces breadth of quality, not just structural correctness.
  const warnCount = findings.filter((f) => f.status === 'warn').length;
  const failCount = findings.filter((f) => f.status === 'fail').length;
  if (failCount >= 2) score = Math.min(score, 60);
  else if (warnCount >= 16) score = Math.min(score, 72);
  else if (warnCount >= 11) score = Math.min(score, 82);
  else if (warnCount >= 7) score = Math.min(score, 88);

  score = Math.max(0, Math.min(100, score));

  return {
    score,
    grade: gradeFor(score),
    letterGrade: letterGradeFor(score),
    categories,
    findings,
    recommendations: buildRecommendations(findings, 5),
    parseError: parsed.parseError,
    rulesetVersion: RULESET_VERSION,
    assessedAt: new Date(),
  };
}

/**
 * Convenience entry point for callers that hold a structured Skill object
 * (e.g. strongly-ai-frontend). Equivalent to:
 *   scoreSkill(skillToScorerInput(skill))
 */
export function scoreSkillObject(skill: SkillLike): ScoreReport {
  return scoreSkill(skillToScorerInput(skill));
}
